import { useRef, useEffect } from 'react';
import { useQuery } from 'hooks/useCustomQuery';
import getCompanyPlatformNumbers from 'API/Company/getCompanyPlatformNumbers';
import getPartnerPlatformNumbers from 'API/Partner/getPartnerPlatformNumbers';
import ScopeType from 'utils/enums/ScopeType';

// NOTE: Wholesaler/Global scope support is intentionally not implemented yet —
// the backend endpoint for those scopes does not exist. Company and Partner
// are the only scopes currently supported for platform numbers (the Navbar
// scope dropdown is likewise restricted to Company/Partner on the Numbers page).
const useGetCompanyPlatformNumbers = (
    currentCompany,
    currentPartner,
    selectedOption,
    currentScope,
    platformList,
    setPlatformsTableData,
    setSummaryData,
    platformNumbersDataFetched,
) => {
    const setPlatformsTableDataRef = useRef(setPlatformsTableData);
    setPlatformsTableDataRef.current = setPlatformsTableData;

    const setSummaryDataRef = useRef(setSummaryData);
    setSummaryDataRef.current = setSummaryData;

    const platformId =
        platformList?.length === 1 ? platformList[0]?.id : selectedOption?.id;

    const enabled =
        (platformList?.length === 1 && platformList[0]?.id !== undefined) ||
        (selectedOption !== undefined && selectedOption?.id !== undefined);

    const fetchPlatformNumbers = () => {
        switch (currentScope) {
            case ScopeType.PARTNER:
                return getPartnerPlatformNumbers(
                    currentPartner,
                    platformId,
                    currentScope,
                );
            default:
                return getCompanyPlatformNumbers(
                    currentCompany,
                    platformId,
                    currentScope,
                );
        }
    };

    const query = useQuery({
        queryKey: [
            'getCompanyPlatformNumbers',
            currentCompany,
            currentPartner,
            platformId,
            currentScope,
        ],
        queryFn: fetchPlatformNumbers,
        refetchOnWindowFocus: false,
        staleTime: 0,
        enabled,
    });

    useEffect(() => {
        if (!query.data) return;
        setPlatformsTableDataRef.current((prev) => ({
            ...prev,
            data: query.data.platformNumbers,
        }));
        platformNumbersDataFetched.current = true;
    }, [query.data, platformNumbersDataFetched]);

    // The summary is shared with the general numbers query, so it is only
    // written while a platform is the view being shown — otherwise these
    // per-platform counts linger over the general list's own counts. `enabled`
    // is a dependency as well as a guard so a stale write cannot survive the
    // switch back when the refetch returns an identical payload (React Query
    // keeps the same data reference).
    useEffect(() => {
        if (!query.data || !enabled) return;
        setSummaryDataRef.current((prev) => ({
            ...prev,
            // The total pill counts the rows this view is showing, which here
            // are the platform numbers the endpoint returned.
            numbers: query.data.platformNumbers.length,
            activePlatformNumbers: query.data.activePlatformNumbers,
            disabledPlatformNumbers: query.data.disabledPlatformNumbers,
            pendingPlatformNumbers: query.data.pendingPlatformNumbers,
        }));
    }, [query.data, enabled]);

    return query;
};

export default useGetCompanyPlatformNumbers;
